import pluralize from 'pluralize';
import React, { memo } from 'react';

import { TextButton } from '@components/Button/variants';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, click } from '@tracking';

interface ArticleCommentJumpLinkProps {
  commentsCount: number;
  commenters?: string[];
  onClick: () => void;
}

const ArticleCommentJumpLink = ({
  commentsCount,
  commenters,
  onClick,
}: ArticleCommentJumpLinkProps): JSX.Element => {
  const { trackEvent } = useEventTracking();

  const constructCommentersAndCountText = (): string => {
    const remainingComments = Math.max(0, commentsCount - commenters.length);
    const pluralizedRemainder = `${remainingComments} ${pluralize('other', remainingComments)}`;

    if (remainingComments === 0) {
      return commenters.join(' and ');
    }

    const possibleComma = commenters.length > 1 ? ',' : '';
    const possibleAnd = commenters.length === 0 ? '' : ' and ';

    return `${commenters.join(', ')}${possibleComma}${possibleAnd}${pluralizedRemainder}`;
  };

  const handleClick = () => {
    onClick();
    trackEvent(
      click({
        clickTarget: ClickTarget.JumpToComments,
        clickText: constructCommentersAndCountText(),
      })
    );
  };

  return (
    <TextButton
      as="button"
      className="hidden border-0 p-0 font-suisse-intl text-overline-sm !text-grayscale-gray no-underline hover:!text-primary-magenta md:block xl:text-overline-md"
      onClick={handleClick}
    >
      {pluralize('Comment', commentsCount)} by {constructCommentersAndCountText()}
    </TextButton>
  );
};

export default memo(ArticleCommentJumpLink);
